import { useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  TestCase,
  TestSuite,
  TestCaseType,
  TestCasePriority,
  TestResultStatus,
  WorkItem,
} from "@zworks/console-contracts";
import * as React from "react";
import { EmptyState } from "@/components/states";
import { PageHeader } from "@/components/bespoke";
import { QualityMetricsPanel } from "@/components/quality-metrics-panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCurrentProject } from "@/lib/project";

type TestCaseWithRequirements = TestCase & {
  requirementWorkItemIds?: string[];
  lastResultStatus?: TestResultStatus;
};

const typeLabels: Record<TestCaseType, string> = {
  manual: "Manual",
  "agent-web": "Automated by agent",
  "agent-api": "API, by agent",
};

const priorityLabels: Record<TestCasePriority, string> = {
  P0: "P0",
  P1: "P1",
  P2: "P2",
  P3: "P3",
};

function typeBadge(type: TestCaseType) {
  return <Badge variant="info">{typeLabels[type]}</Badge>;
}

function priorityBadge(priority: TestCasePriority) {
  return <Badge variant="outline">{priorityLabels[priority]}</Badge>;
}

function statusBadge(status: TestResultStatus | undefined) {
  if (status === undefined) return <Badge variant="muted">Untested</Badge>;
  return <Badge variant="success">{status}</Badge>;
}

function Heading({ description, actions }: { description?: string; actions?: React.ReactNode }) {
  return <PageHeader title="Test Cases" description={description} actions={actions} />;
}

export function TestCasesPage() {
  const { project, loaded } = useCurrentProject();
  const projectId = project?.id;
  const [search, setSearch] = React.useState("");
  const [dialog, setDialog] = React.useState<"none" | "new" | "edit">("none");
  const [editing, setEditing] = React.useState<TestCaseWithRequirements | null>(null);
  const [selectedSuiteId, setSelectedSuiteId] = React.useState<string | null>(null);
  const [newCaseKey, setNewCaseKey] = React.useState<string | null>(null);
  const [newCaseTitle, setNewCaseTitle] = React.useState<string | null>(null);
  const [newCaseType, setNewCaseType] = React.useState<TestCaseType>("manual");
  const [newCasePriority, setNewCasePriority] = React.useState<TestCasePriority>("P1");
  const [newCaseSteps, setNewCaseSteps] = React.useState<
    { order: number; action: string; expected?: string }[]
  >([]);
  const [newCaseExpectedResult, setNewCaseExpectedResult] = React.useState<string | null>(null);
  const [newCaseTags, setNewCaseTags] = React.useState<string[]>([]);
  const [newCaseScriptPath, setNewCaseScriptPath] = React.useState<string | null>(null);
  const [newCaseRequirementIds, setNewCaseRequirementIds] = React.useState<string[]>([]);
  const queryClient = useQueryClient();

  // Load suites
  const suitesQuery = useQuery({
    queryKey: ["projects", projectId, "quality", "suites"],
    queryFn: async () => {
      if (!projectId) return [];
      const res = await fetch(`/api/v1/projects/${projectId}/quality/suites`, {
        method: "GET",
        credentials: "same-origin",
      });
      if (!res.ok) return [];
      return (await res.json()) as TestSuite[];
    },
    enabled: loaded && projectId !== null,
  });

  // Load cases filtered by suite
  const casesQuery = useQuery({
    queryKey: ["projects", projectId, "quality", "cases", selectedSuiteId ?? "all", search],
    queryFn: async () => {
      if (!projectId || !project) return [];
      const suiteFilter = selectedSuiteId ?? undefined;
      const res = await fetch(
        `/api/v1/projects/${projectId}/quality/cases?suiteId=${suiteFilter}&search=${encodeURIComponent(search)}`,
        { method: "GET", credentials: "same-origin" },
      );
      if (!res.ok) return [];
      return (await res.json()) as TestCaseWithRequirements[];
    },
    enabled: loaded && projectId !== null,
  });

  // Load work items for requirement linking
  const workItemsQueryResult = useQuery({
    queryKey: ["projects", projectId, "work-items", "all"],
    queryFn: async () => {
      if (!projectId) return [];
      const res = await fetch(`/api/v1/projects/${projectId}/work-items?status=all&search=`, {
        method: "GET",
        credentials: "same-origin",
      });
      if (!res.ok) return [];
      const data = await res.json();
      return (data.items || []) as WorkItem[];
    },
    enabled: loaded && projectId !== null,
  });

  function handleDialogOpen(type: "new" | "edit") {
    setDialog(type);
  }

  function handleDialogClose() {
    setDialog("none");
    setEditing(null);
    setNewCaseKey(null);
    setNewCaseTitle(null);
    setNewCaseType("manual");
    setNewCasePriority("P1");
    setNewCaseSteps([]);
    setNewCaseExpectedResult(null);
    setNewCaseTags([]);
    setNewCaseScriptPath(null);
    setNewCaseRequirementIds([]);
    queryClient.invalidateQueries({ queryKey: ["projects", projectId, "quality", "cases"] });
  }

  function addStep() {
    const maxOrder =
      newCaseSteps.length > 0 ? Math.max(...newCaseSteps.map((s) => s.order)) + 1 : 1;
    setNewCaseSteps([...newCaseSteps, { order: maxOrder, action: "", expected: "" }]);
  }

  function removeStep(index: number) {
    setNewCaseSteps(newCaseSteps.filter((_, i) => i !== index));
  }

  // Render suite selector options
  const suiteOptions =
    suitesQuery.data?.map((s) => ({
      value: s.id,
      label: s.name,
    })) ?? [];

  // Render case rows
  const cases = casesQuery.data ?? [];
  const filteredCases = cases.filter((c) => c.title.toLowerCase().includes(search.toLowerCase()));

  if (!loaded || projectId === null || project === null) {
    return (
      <EmptyState
        title="Test Cases"
        description="Create a project to start managing test cases."
        action={{
          label: "Create Project",
          onClick: () => setSearch(""),
        }}
      />
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Heading
        description={project.name}
        actions={
          <div className="flex gap-2">
            <Button onClick={() => handleDialogOpen("new")}>New test case</Button>
          </div>
        }
      />

      <QualityMetricsPanel projectId={project.id} />

      {/* Suite selector */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm text-muted-foreground">Suite:</span>
            {suiteOptions.length > 0 ? (
              <Select
                value={selectedSuiteId ?? ""}
                onValueChange={(v) => setSelectedSuiteId(v || null)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a suite" />
                </SelectTrigger>
                <SelectContent>
                  {suiteOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <span className="text-muted-foreground">No suites yet.</span>
            )}
          </div>

          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary">
              <svg className="ico" style={{ width: 14, height: 14 }}>
                <use href="#i-plus" />
              </svg>{" "}
              New suite
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Case table and actions */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <caption className="sr-only">Test cases</caption>
              <thead>
                <tr>
                  {[
                    "ID",
                    "Title",
                    "Type",
                    "Priority",
                    "Last Result",
                    "Tags",
                    "Requirements",
                    "Actions",
                  ].map((header) => (
                    <th
                      key={header}
                      scope="col"
                      className="h-10 border-b px-3 text-left text-[0.8125rem] font-medium whitespace-nowrap text-muted-foreground first:pl-5 last:pr-5"
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredCases.map((case_) => (
                  <tr key={case_.id} className="group hover:bg-muted/50">
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5 font-mono text-[0.8125rem] font-medium">
                      {case_.key}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <b>{case_.title}</b>
                      <div className="text-muted-foreground text-xs">{case_.type}</div>
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {typeBadge(case_.type)}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {priorityBadge(case_.priority)}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {statusBadge(case_.lastResultStatus)}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {case_.tags && case_.tags.length > 0 ? (
                        case_.tags.map((t) => (
                          <Badge key={t} variant="secondary" className="mr-1">
                            {t}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-muted-foreground text-xs">None</span>
                      )}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      {case_.requirementWorkItemIds && case_.requirementWorkItemIds.length > 0 ? (
                        case_.requirementWorkItemIds.map((wiId) => {
                          const wi = (workItemsQueryResult.data || []).find((w) => w.id === wiId);
                          return wi ? (
                            <a
                              key={wiId}
                              href={`/work-items/${wi.key}`}
                              className="text-info underline hover:opacity-80 mr-2"
                            >
                              {wi.key}
                            </a>
                          ) : (
                            <span key={wiId} className="text-muted-foreground text-xs mr-2">
                              WI-{wiId.slice(-6)}
                            </span>
                          );
                        })
                      ) : (
                        <span className="text-muted-foreground text-xs">None</span>
                      )}
                    </td>
                    <td className="border-b px-3 py-2.5 align-middle whitespace-nowrap group-last:border-b-0 first:pl-5 last:pr-5">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditing(case_);
                          handleDialogOpen("edit");
                        }}
                      >
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filteredCases.length === 0 && (
            <div className="p-8 text-center text-muted-foreground">
              No test cases found. Create your first test case.
            </div>
          )}
        </CardContent>
      </Card>

      {/* New case dialog */}
      {dialog === "new" && (
        <Dialog open={true} onOpenChange={(open) => !open && handleDialogClose()}>
          <DialogContent className="px-6 pb-4 max-w-3xl">
            <DialogTitle>New Test Case</DialogTitle>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title</label>
                <Input
                  placeholder="Enter test case title"
                  value={newCaseTitle ?? ""}
                  onChange={(e) => setNewCaseTitle(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Key</label>
                <Input
                  placeholder="TC-123"
                  value={newCaseKey ?? ""}
                  onChange={(e) => setNewCaseKey(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="agent-web">Automated by agent</SelectItem>
                    <SelectItem value="agent-api">API, by agent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Priority</label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="P0">P0</SelectItem>
                    <SelectItem value="P1">P1</SelectItem>
                    <SelectItem value="P2">P2</SelectItem>
                    <SelectItem value="P3">P3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Suite</label>
                <Select>
                  <SelectTrigger>
                    <SelectValue placeholder="Select suite" />
                  </SelectTrigger>
                  <SelectContent>
                    {suiteOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Tags (comma-separated)</label>
                <Input
                  placeholder="tag1,tag2,tag3"
                  value={newCaseTags.join(",")}
                  onChange={(e) =>
                    setNewCaseTags(e.target.value.split(",").filter((s: string) => s.trim()))
                  }
                />
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Steps</label>
              <div className="space-y-2">
                {newCaseSteps.map(
                  (step: { order: number; action: string; expected?: string }, i) => (
                    <div key={i} className="flex gap-2">
                      <Input
                        type="number"
                        value={step.order.toString()}
                        onChange={(e) => {
                          const newSteps = [...newCaseSteps];
                          const targetStep = newSteps[i];
                          if (targetStep) targetStep.order = Number(e.target.value);
                          setNewCaseSteps(newSteps);
                        }}
                        min="1"
                        className="w-16"
                      />
                      <Input
                        placeholder="Step action"
                        value={step.action}
                        onChange={(e) => {
                          const newSteps = [...newCaseSteps];
                          const targetStep = newSteps[i];
                          if (targetStep) targetStep.action = e.target.value;
                          setNewCaseSteps(newSteps);
                        }}
                      />
                      <Input
                        placeholder="Expected result (optional)"
                        value={step.expected ?? ""}
                        onChange={(e) => {
                          const newSteps = [...newCaseSteps];
                          const targetStep = newSteps[i];
                          if (targetStep) targetStep.expected = e.target.value;
                          setNewCaseSteps(newSteps);
                        }}
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        className="p-1"
                        onClick={() => removeStep(i)}
                      >
                        <svg className="ico" style={{ width: 14, height: 14 }}>
                          <use href="#i-x" />
                        </svg>
                      </Button>
                    </div>
                  ),
                )}
                <Button size="icon" variant="ghost" className="p-1" onClick={() => addStep()}>
                  <svg className="ico" style={{ width: 14, height: 14 }}>
                    <use href="#i-plus" />
                  </svg>
                </Button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Expected Result</label>
              <Textarea
                placeholder="What should happen after these steps"
                value={newCaseExpectedResult ?? ""}
                onChange={(e) => setNewCaseExpectedResult(e.target.value)}
                className="w-full min-h-[80px]"
              />
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">
                Script Path (for automated cases)
              </label>
              <Input
                placeholder="path/to/script.js (optional, for agent-web/agent-api)"
                value={newCaseScriptPath ?? ""}
                onChange={(e) => setNewCaseScriptPath(e.target.value)}
              />
              <p className="text-muted-foreground text-xs mt-1">
                Provide a path to a Playwright script in the customer repository. The repository is
                the source of truth for code.
              </p>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Requirement Links</label>
              <div className="space-y-2">
                {newCaseRequirementIds.map((wiId) => (
                  <div key={wiId} className="flex items-center gap-2">
                    <Input
                      value={
                        (workItemsQueryResult.data || []).find((w) => w.id === wiId)?.key ?? ""
                      }
                      readOnly
                      className="flex-1 min-w-0"
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="p-1"
                      onClick={() => {
                        const newIds = newCaseRequirementIds.filter((id) => id !== wiId);
                        setNewCaseRequirementIds(newIds);
                      }}
                    >
                      <svg className="ico" style={{ width: 14, height: 14 }}>
                        <use href="#i-x" />
                      </svg>
                    </Button>
                  </div>
                ))}
                {workItemsQueryResult.data && workItemsQueryResult.data.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Link WI..."
                      onChange={(e) => {
                        const val = e.target.value.trim();
                        if (val && !newCaseRequirementIds.includes(val)) {
                          setNewCaseRequirementIds([...newCaseRequirementIds, val]);
                        }
                      }}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="p-1"
                      onClick={() => setNewCaseRequirementIds([])}
                    >
                      <svg className="ico" style={{ width: 14, height: 14 }}>
                        <use href="#i-x" />
                      </svg>
                    </Button>
                  </div>
                )}
                {workItemsQueryResult.data && workItemsQueryResult.data.length === 0 && (
                  <p className="text-muted-foreground text-xs">
                    No work items found. Add work items first.
                  </p>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <Button size="sm" variant="secondary" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="default"
                onClick={() => {
                  if (!projectId) return;
                  const input = {
                    suiteId: selectedSuiteId ?? undefined,
                    sectionId: null,
                    key: newCaseKey ?? `TC-${Date.now()}`,
                    title: newCaseTitle ?? "New test case",
                    type: newCaseType,
                    priority: newCasePriority,
                    steps: newCaseSteps,
                    expectedResult: newCaseExpectedResult ?? null,
                    scriptPath: newCaseScriptPath ?? null,
                    tags: newCaseTags,
                  };
                  fetch(`/api/v1/projects/${projectId}/quality/cases`, {
                    method: "POST",
                    credentials: "same-origin",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(input),
                  }).then(() => {
                    handleDialogClose();
                    queryClient.invalidateQueries({
                      queryKey: ["projects", projectId, "quality", "cases"],
                    });
                  });
                }}
              >
                Create
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit case dialog */}
      {dialog === "edit" && editing && (
        <Dialog open={true} onOpenChange={(open) => !open && handleDialogClose()}>
          <DialogContent className="px-6 pb-4 max-w-3xl">
            <DialogTitle>Edit Test Case</DialogTitle>

            <Input
              placeholder="Enter test case title"
              value={editing.title ?? ""}
              onChange={(e) => setNewCaseTitle(e.target.value)}
            />
            <Input
              placeholder="TC-123"
              value={editing.key ?? ""}
              onChange={(e) => setNewCaseKey(e.target.value)}
            />
            <Select>
              <SelectTrigger>
                <SelectValue placeholder="Select type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="agent-web">Automated by agent</SelectItem>
                <SelectItem value="agent-api">API, by agent</SelectItem>
              </SelectContent>
            </Select>
            <Select>
              <SelectTrigger>
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="P0">P0</SelectItem>
                <SelectItem value="P1">P1</SelectItem>
                <SelectItem value="P2">P2</SelectItem>
                <SelectItem value="P3">P3</SelectItem>
              </SelectContent>
            </Select>
            <Input
              placeholder="path/to/script.js (optional)"
              value={editing.scriptPath ?? ""}
              onChange={(e) => setNewCaseScriptPath(e.target.value)}
            />
            <Textarea
              placeholder="What should happen after these steps"
              value={editing.expectedResult ?? ""}
              onChange={(e) => setNewCaseExpectedResult(e.target.value)}
              className="w-full min-h-[80px]"
            />
            <div className="space-y-2">
              {editing.tags?.map((t: string, i: number) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={t} readOnly className="flex-1 min-w-0" />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="p-1"
                    onClick={() => {
                      const newTags = editing.tags!.filter((_: string, idx: number) => idx !== i);
                      setNewCaseTags(newTags);
                    }}
                  >
                    <svg className="ico" style={{ width: 14, height: 14 }}>
                      <use href="#i-x" />
                    </svg>
                  </Button>
                </div>
              ))}
              {workItemsQueryResult.data && workItemsQueryResult.data.length > 0 && (
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Link WI..."
                    onChange={(e) => {
                      const val = e.target.value.trim();
                      if (val && !newCaseRequirementIds.includes(val)) {
                        setNewCaseRequirementIds([...newCaseRequirementIds, val]);
                      }
                    }}
                  />
                  <Button
                    size="icon"
                    variant="ghost"
                    className="p-1"
                    onClick={() => setNewCaseRequirementIds([])}
                  >
                    <svg className="ico" style={{ width: 14, height: 14 }}>
                      <use href="#i-x" />
                    </svg>
                  </Button>
                </div>
              )}
            </div>

            <div className="space-y-2">
              {editing.steps?.map(
                (step: { order: number; action: string; expected?: string }, i) => (
                  <div key={i} className="flex gap-2">
                    <Input
                      type="number"
                      value={step.order.toString()}
                      onChange={(e) => {
                        const newSteps = [...(editing.steps || [])];
                        const targetStep = newSteps[i];
                        if (targetStep) targetStep.order = Number(e.target.value);
                        setEditing({ ...editing, steps: newSteps } as TestCaseWithRequirements);
                      }}
                      min="1"
                      className="w-16"
                    />
                    <Input
                      placeholder="Step action"
                      value={step.action}
                      onChange={(e) => {
                        const newSteps = [...(editing.steps || [])];
                        const targetStep = newSteps[i];
                        if (targetStep) targetStep.action = e.target.value;
                        setEditing({ ...editing, steps: newSteps } as TestCaseWithRequirements);
                      }}
                    />
                    <Input
                      placeholder="Expected (optional)"
                      value={step.expected ?? ""}
                      onChange={(e) => {
                        const newSteps = [...(editing.steps || [])];
                        const targetStep = newSteps[i];
                        if (targetStep) targetStep.expected = e.target.value;
                        setEditing({ ...editing, steps: newSteps } as TestCaseWithRequirements);
                      }}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="p-1"
                      onClick={() => {
                        const newSteps = [...(editing.steps || [])];
                        newSteps.splice(i, 1);
                        setEditing({ ...editing, steps: newSteps } as TestCaseWithRequirements);
                      }}
                    >
                      <svg className="ico" style={{ width: 14, height: 14 }}>
                        <use href="#i-x" />
                      </svg>
                    </Button>
                  </div>
                ),
              )}
            </div>

            <Button size="icon" variant="ghost" className="p-1" onClick={() => addStep()}>
              <svg className="ico" style={{ width: 14, height: 14 }}>
                <use href="#i-plus" />
              </svg>
            </Button>

            <div className="flex gap-3">
              <Button size="sm" variant="secondary" onClick={handleDialogClose}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="default"
                onClick={() => {
                  if (!projectId || !editing.id) return;
                  const input: Record<string, unknown> = {
                    title: newCaseTitle,
                    key: newCaseKey,
                    type: newCaseType,
                    priority: newCasePriority,
                    expectedResult: newCaseExpectedResult ?? null,
                    scriptPath: newCaseScriptPath ?? null,
                  };
                  if (newCaseTags && newCaseTags.length > 0) {
                    input.tags = newCaseTags;
                  }
                  if (newCaseSteps && newCaseSteps.length > 0) {
                    input.steps = newCaseSteps;
                  }
                  fetch(`/api/v1/projects/${projectId}/quality/cases/${editing.id}`, {
                    method: "PATCH",
                    credentials: "same-origin",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(input),
                  }).then(() => {
                    handleDialogClose();
                  });
                }}
              >
                Save
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
